import { Row } from '@stenajs-webui/core';
import type { FC } from 'react';
import { MAX_LENGTH_EMPTY_VEHICLE_WEIGHT } from '../../../../../common/fieldLengths';
import { useUpdateRegistrationNumberAlertApplied } from '../../../../../common/registrationNumberAlert/hooks/useUpdateRegistrationNumberAlertApplied';
import { useUpdateField } from '../../../../../common/update-information/hooks/useUpdateField';
import { type BookingFields, isHidden } from '../../../../../common/utils/bookingFields';
import { transformNumberOfDrivers } from '../../../../../common/utils/optionTransformers';
import { DetailsFieldCell } from '../../../../../common-ui/details/DetailsFieldCell';
import { HiddenFieldGate } from '../../../../../common-ui/HiddenFieldGate';
import { TemperatureInformationIconPopover } from '../../../../../common-ui/information-message/TemperatureInformationIconPopover';
import { ColumnId } from '../../../../../gql/graphql';
import type { BookingFormState } from '../../../../../services/update-form/updateFormReducer';
import { DimensionsFieldGrid } from '../edit/BookingDetailsEditContent';
import { DropdownField } from '../fields/DropdownField';
import { NumericField } from '../fields/NumericField';
import { RegistrationNoField } from '../fields/RegistrationNoField';
import { TextInputField } from '../fields/TextInputField';
import { VehicleTypeField } from '../VehicleTypeField';
import { BookingSection } from './BookingSection';
import { personsRange } from './common';

interface VehicleTrailerSectionProps {
  disabled: boolean;
  fieldsToEnable: BookingFields;
  fieldsToShow: BookingFields;
  formState: BookingFormState;
}

export const VehicleDetailsSection: FC<VehicleTrailerSectionProps> = ({
  disabled,
  fieldsToShow,
  fieldsToEnable,
  formState,
}) => {
  const { editedValue: editedLength } = useUpdateField<'length'>('length');
  const { editedValue: editedHeight } = useUpdateField<'height'>('height');
  const { arrivalNotepadApplied, icon: heightIcon } = useUpdateRegistrationNumberAlertApplied(
    'Details',
    'height',
    editedHeight,
  );
  const { icon: lengthIcon } = useUpdateRegistrationNumberAlertApplied(
    'Details',
    'length',
    editedLength,
  );

  const isDisabled = (field: ColumnId) => disabled || isHidden(fieldsToEnable, field);

  return (
    <BookingSection title={'Vehicle'}>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.VehicleRegNo)}>
        <DetailsFieldCell
          label={'Vehicle registration'}
          renderField={id => (
            <RegistrationNoField
              id={id}
              type={'vehicleRegNo'}
              disabled={isDisabled(ColumnId.VehicleRegNo)}
            />
          )}
        />
      </HiddenFieldGate>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.TrailerRegNo)}>
        <DetailsFieldCell
          label={'Trailer registration'}
          renderField={id => (
            <RegistrationNoField
              id={id}
              type={'trailerRegNo'}
              disabled={isDisabled(ColumnId.TrailerRegNo)}
            />
          )}
        />
      </HiddenFieldGate>
      <Row gap={2}>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.VehicleTypeCode)}>
          <DetailsFieldCell
            flex={'3 1 100px'}
            label={'Vehicle type'}
            renderField={id => (
              <VehicleTypeField
                id={id}
                disabled={arrivalNotepadApplied || isDisabled(ColumnId.VehicleTypeCode)}
                customerNo={formState.customer?.custNo ?? null}
                routeCode={formState.route?.code ?? null}
              />
            )}
          />
        </HiddenFieldGate>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.NoOfDrivers)}>
          <DetailsFieldCell
            flex={'1 1 50px'}
            label={'Drivers'}
            renderField={id => (
              <DropdownField
                id={id}
                trackerCategory={'Details'}
                name={'noOfDrivers'}
                transformOption={transformNumberOfDrivers}
                options={personsRange}
                disabled={isDisabled(ColumnId.NoOfDrivers)}
              />
            )}
          />
        </HiddenFieldGate>
      </Row>
      <DimensionsFieldGrid>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.Length)}>
          <DetailsFieldCell
            label={'Length (m)'}
            renderField={id => (
              <NumericField
                id={id}
                className={'t_length_details_edit'}
                trackerCategory={'Details'}
                name={'length'}
                disabled={arrivalNotepadApplied || isDisabled(ColumnId.Length)}
                buttonRight={lengthIcon}
              />
            )}
          />
        </HiddenFieldGate>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.Width)}>
          <DetailsFieldCell
            label={'Width (m)'}
            renderField={id => (
              <NumericField
                id={id}
                className={'t_width_details_edit'}
                name={'width'}
                disabled={isDisabled(ColumnId.Width)}
                trackerCategory={'Details'}
              />
            )}
          />
        </HiddenFieldGate>
        <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.Height)}>
          <DetailsFieldCell
            label={'Height (m)'}
            renderField={id => (
              <NumericField
                id={id}
                className={'t_height_details_edit'}
                trackerCategory={'Details'}
                name={'height'}
                disabled={arrivalNotepadApplied || isDisabled(ColumnId.Height)}
                buttonRight={heightIcon}
              />
            )}
          />
        </HiddenFieldGate>
      </DimensionsFieldGrid>
      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.Temperature)}>
        <DimensionsFieldGrid>
          <DetailsFieldCell
            label={'Temperature onboard (°C)'}
            contentRight={
              <Row flex={1} alignItems={'start'}>
                <TemperatureInformationIconPopover />
              </Row>
            }
            renderField={id => (
              <TextInputField
                id={id}
                name={'pluginTemperature'}
                trackerCategory={'Details'}
                disabled={isDisabled(ColumnId.Temperature)}
              />
            )}
          />
        </DimensionsFieldGrid>
      </HiddenFieldGate>

      <HiddenFieldGate hidden={isHidden(fieldsToShow, ColumnId.TradeWeight)}>
        <DetailsFieldCell
          label={'Trade weight (kg)'}
          renderField={id => (
            <NumericField
              id={id}
              trackerCategory={'Details'}
              name={'tradeWeight'}
              disabled={isDisabled(ColumnId.TradeWeight)}
              maxLength={MAX_LENGTH_EMPTY_VEHICLE_WEIGHT}
              onlyIntegers
            />
          )}
        />
      </HiddenFieldGate>
    </BookingSection>
  );
};
